import React, { useMemo } from 'react';
import './DownloadPage.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/bluewire-app/id6756771312';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.bluewire.app';

function getPlatformPreference() {
  if (typeof navigator === 'undefined') return 'unknown';

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const touchMac = platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  if (/android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(userAgent) || touchMac) return 'ios';

  return 'unknown';
}

export default function DownloadPage() {
  const platformPreference = useMemo(() => getPlatformPreference(), []);
  const storeButtons = [
    {
      id: 'google',
      label: 'Get it on',
      store: 'Google Play',
      href: GOOGLE_PLAY_URL,
      priority: platformPreference === 'android' ? 0 : 1,
    },
    {
      id: 'apple',
      label: 'Download on the',
      store: 'App Store',
      href: APP_STORE_URL,
      priority: platformPreference === 'ios' ? 0 : 1,
    },
  ].sort((a, b) => a.priority - b.priority);

  return (
    <main className="download-page">
      <section className="download-hero" aria-labelledby="download-title">
        <div className="download-shell">
          <div className="download-brand">
            <img
              src="/bluewire-messaging-logo.png"
              alt="BlueWire logo"
              className="download-logo"
            />
            <h1 id="download-title">BlueWire</h1>
            <p className="download-brand-line">Connect <span aria-hidden="true">·</span> Communicate <span aria-hidden="true">·</span> Collaborate</p>
            <p className="download-tagline">
              Secure communication, intelligence sharing, and professional networking for law enforcement.
            </p>
          </div>

          <div className="download-card">
            <p className="download-intro">
              BlueWire allows law enforcement professionals to connect across agencies, making it easier to
              communicate, share information, and create new contacts.
            </p>
            <ul className="download-feature-list" aria-label="BlueWire app features">
              <li>Create and manage field notes</li>
              <li>Share and receive BOLOs and intelligence alerts</li>
              <li>Search for verified professionals across agencies</li>
              <li>Communicate through direct messaging, group messaging, and voice calls</li>
              <li>Participate in professional discussions through The Wire</li>
              <li>Stay informed with national and local law enforcement-related news</li>
            </ul>

            <section className="download-section" aria-labelledby="verification-title">
              <p className="download-eyebrow">Built for verified law enforcement professionals.</p>
              <h2 id="verification-title">Three-Part Verification Process</h2>
              <ol className="verification-list">
                <li>Work Email Verification</li>
                <li>Cell Phone Verification</li>
                <li>Work Credential / Identity Verification</li>
              </ol>
              <p className="download-note">
                This helps ensure that access is restricted to verified law enforcement professionals.
              </p>
            </section>

            <section className="download-section privacy-section" aria-labelledby="privacy-title">
              <h2 id="privacy-title">Privacy Clarification</h2>
              <p>
                Your work email, phone number, and credentials are <u>used for verification only and are <b> not visible to
                other users.</b></u> Connections and communication happen inside BlueWire without exposing your personal contact
                information.
              </p>
            </section>

            <div className="download-actions" aria-label="Download and web access options">
              {storeButtons.map((button) => (
                <a
                  key={button.id}
                  href={button.href}
                  className={`store-button ${button.id === 'apple' ? 'store-button-apple' : 'store-button-google'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {button.id === 'apple' ? (
                    <svg className="store-icon store-icon-apple" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M17.6 13.2c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.8-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.8-.4 7 1.1 9.3.8 1.1 1.7 2.3 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.2.9-1.3 1.2-2.5 1.2-2.6 0-.1-2.6-1.1-2.6-3.8zM15.5 6.6c.6-.8 1-1.8.9-2.8-.9 0-2 .6-2.6 1.3-.6.7-1.1 1.7-1 2.7 1 .1 2-.5 2.7-1.2z"
                      />
                    </svg>
                  ) : (
                    <svg className="store-icon store-icon-google" viewBox="0 0 28 31" aria-hidden="true">
                      <path fill="#00f0ff" d="M2.2 1.1 16.3 15 2.2 29c-.4-.4-.7-1-.7-1.7V2.8c0-.7.3-1.3.7-1.7z" />
                      <path fill="#00d26a" d="m2.2 1.1 18.4 10.5-4.3 3.4L2.2 1.1z" />
                      <path fill="#ffce00" d="m16.3 15 4.3-3.4 4.8 2.8c1.3.7 1.3 2.5 0 3.2l-4.8 2.8-4.3-5.4z" />
                      <path fill="#ff3d71" d="m2.2 29 14.1-14 4.3 5.4L2.2 29z" />
                    </svg>
                  )}
                  <span className="store-copy">
                    <span>{button.label}</span>
                    <strong>{button.store}</strong>
                  </span>
                </a>
              ))}

              <a href="/login" className="web-login-button">
                Open BlueWire on the web
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
